import { useLoading } from '@agney/react-loading';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { Button } from '../../../components/Button';
import Text from '../../../components/Text';
import Title from '../../../components/Title';
import {
  CloseWhite as CloseIcon,
  DownloadWhite as DownloadIcon,
  Files as FilesIcon,
  UploadWhite,
} from '../../../icons';
import MaterialSheetDocument from '../../../models/MaterialSheetDocument';
import { useMaterialSheetStore } from '../../../store/MaterialSheetStore';
import { ONE_MO_SIZE, formatBytes } from '../../../utilities/ByteSizes';
import { downloadFileByURL } from '../../../utilities/DownloadFile';
import StyleData from '../../../utilities/StyleData';
import {
  ActionsRow,
  DocumentActions,
  DocumentInfo,
  DocumentList,
  DocumentRow,
  ErrorMessage,
  HelperText,
  HiddenFileInput,
  Root,
} from './MaterialSheetDocumentsStyle';

export const MATERIAL_SHEET_DOCUMENTS_MAX_FILES = 5;
export const MATERIAL_SHEET_DOCUMENTS_MAX_SIZE = ONE_MO_SIZE;

export const MATERIAL_SHEET_DOCUMENTS_ACCEPT: Accept = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.oasis.opendocument.text': ['.odt'],
  'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
  'image/jpeg': ['.jpg'],
};

const ACCEPTED_EXTENSIONS_LABEL = Object.values(MATERIAL_SHEET_DOCUMENTS_ACCEPT).flat().join(', ');

const ACCEPTED_MIME_LIST = Object.values(MATERIAL_SHEET_DOCUMENTS_ACCEPT).flat().join(',');

const MAX_FILES_MESSAGE = `Vous ne pouvez joindre que ${MATERIAL_SHEET_DOCUMENTS_MAX_FILES} fichiers maximum par fiche matériel.`;

interface MaterialSheetDocumentsProps {
  disabled: boolean;
  loading?: boolean;
}

type DocumentsMode = 'loading' | 'empty' | 'view' | 'edit';

/**
 * Gestion des pièces jointes d'une fiche matériel. Le composant se décline en
 * quatre écrans mutuellement exclusifs exposés via {@link DocumentsMode} :
 *
 * - `empty` — **État 1** : aucun fichier. Drop zone seule + texte d'aide.
 * - `view`  — **État 2** : au moins un fichier. Liste de pills avec bouton
 *             téléchargement et action `Modifier` (bascule vers l'état 3).
 * - `edit`  — **État 3** : mode modification. Drop zone contenant les pills
 *             avec bouton `✕` de suppression individuelle, actions
 *             `Tout effacer` et `Valider la modification`.
 * - `loading` — écran de chargement initial (récupération des pièces jointes
 *               d'une fiche existante).
 *
 * Transitions : 1 → 2 (import) · 2 → 3 (clic Modifier) · 3 → 2 (Valider) ·
 * 3 → 1 (auto quand plus aucun visible, ou Tout effacer).
 */
export function MaterialSheetDocuments({ disabled, loading = false }: MaterialSheetDocumentsProps) {
  const documents = useMaterialSheetStore((state) => state.documents);
  const addPendingDocuments = useMaterialSheetStore((state) => state.addPendingDocuments);
  const clearAllDocuments = useMaterialSheetStore((state) => state.clearAllDocuments);
  const markDocumentForDeletion = useMaterialSheetStore((state) => state.markDocumentForDeletion);
  const removePendingDocument = useMaterialSheetStore((state) => state.removePendingDocument);

  const [error, setError] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const visibleDocuments = useMemo(() => documents.filter((d) => !d.toDelete), [documents]);
  const remainingSlots = MATERIAL_SHEET_DOCUMENTS_MAX_FILES - visibleDocuments.length;

  useEffect(() => {
    if (isEditing && visibleDocuments.length === 0) setIsEditing(false);
  }, [isEditing, visibleDocuments.length]);

  const validateAndAdd = (accepted: File[], rejections: FileRejection[] = []): void => {
    if (rejections.length > 0) {
      const first = rejections[0];
      const code = first.errors[0]?.code;
      if (code === 'file-too-large') {
        setError(`Fichier "${first.file.name}" rejeté : taille maximale dépassée (1 Mo).`);
      } else if (code === 'file-invalid-type') {
        setError(`Fichier "${first.file.name}" rejeté, extensions acceptées : ${ACCEPTED_EXTENSIONS_LABEL}.`);
      } else {
        setError(`Fichier "${first.file.name}" rejeté.`);
      }
      return;
    }
    const tooLarge = accepted.find((f) => f.size > MATERIAL_SHEET_DOCUMENTS_MAX_SIZE);
    if (tooLarge) {
      setError(`Fichier "${tooLarge.name}" rejeté : taille maximale dépassée (1 Mo).`);
      return;
    }
    if (accepted.length > remainingSlots) {
      setError(MAX_FILES_MESSAGE);
      return;
    }
    setError(undefined);
    addPendingDocuments(accepted);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    accept: MATERIAL_SHEET_DOCUMENTS_ACCEPT,
    maxSize: MATERIAL_SHEET_DOCUMENTS_MAX_SIZE,
    maxFiles: remainingSlots > 0 ? remainingSlots : 0,
    disabled: disabled || remainingSlots <= 0,
    onDrop: (accepted, rejections) => validateAndAdd(accepted, rejections),
    noClick: true,
    noKeyboard: true,
  });

  const openPicker = () => hiddenInputRef.current?.click();

  const onClearAll = () => {
    clearAllDocuments();
    setIsEditing(false);
    setError(undefined);
  };

  const onRemoveDocument = (doc: MaterialSheetDocument) => {
    if (doc.id !== undefined) markDocumentForDeletion(doc.id);
    else removePendingDocument(doc);
    setError(undefined);
  };

  const mode: DocumentsMode = loading
    ? 'loading'
    : visibleDocuments.length === 0
    ? 'empty'
    : isEditing
    ? 'edit'
    : 'view';

  const pills =
    mode === 'view' || mode === 'edit' ? (
      <DocumentList>
        {visibleDocuments.map((doc, index) => {
          const canDownload = doc.isPersisted() && !!doc.documentUri;
          return (
            <DocumentRow key={doc.id ?? `pending-${index}-${doc.originalName}`}>
              <DocumentInfo>
                <span className="name" title={doc.originalName}>
                  {doc.originalName}
                </span>
                <span className="size">{formatBytes(doc.size)}</span>
              </DocumentInfo>
              <DocumentActions>
                {mode === 'edit' ? (
                  <button type="button" title="Supprimer" onClick={() => onRemoveDocument(doc)}>
                    <CloseIcon />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!canDownload}
                    title="Télécharger"
                    onClick={() =>
                      canDownload &&
                      downloadFileByURL((process.env.REACT_APP_URL_API ?? '') + doc.documentUri)
                    }
                  >
                    <DownloadIcon />
                  </button>
                )}
              </DocumentActions>
            </DocumentRow>
          );
        })}
      </DocumentList>
    ) : null;

  return (
    <Root>
      <Title level={3} name="Pièces jointes" svg={<FilesIcon width={20} height={20} />} />
      {mode === 'loading' && <InitialLoader />}

      {mode === 'empty' && (
        <>
          <FileDropZone
            disabled={disabled}
            rootProps={getRootProps()}
            inputProps={getInputProps()}
            isDragActive={isDragActive}
            onBrowseClick={openPicker}
          />
          <FormatsHelperText />
        </>
      )}

      {mode === 'view' && (
        <>
          {pills}
          {!disabled && (
            <ActionsRow>
              <Button variant="secondarySmallOutline" callback={() => setIsEditing(true)} skipResponsive>
                Modifier
              </Button>
              <Button
                variant="secondarySmall"
                icon={UploadWhite}
                callback={remainingSlots <= 0 ? undefined : openPicker}
                disabled={remainingSlots <= 0}
                skipResponsive
              >
                Importer des fichiers
              </Button>
            </ActionsRow>
          )}
        </>
      )}

      {mode === 'edit' && (
        <>
          <DropZoneContainer {...getRootProps()} isActive={isDragActive} isDisabled={disabled}>
            <input {...getInputProps()} />
            {pills}
          </DropZoneContainer>
          {!disabled && (
            <ActionsRow>
              <Button variant="secondarySmallOutline" callback={onClearAll} skipResponsive>
                Tout effacer
              </Button>
              <Button variant="primarySmall" callback={() => setIsEditing(false)} skipResponsive>
                Valider la modification
              </Button>
            </ActionsRow>
          )}
        </>
      )}

      {!disabled && (
        <HiddenFileInput
          ref={hiddenInputRef}
          type="file"
          multiple
          accept={ACCEPTED_MIME_LIST}
          onChange={(ev) => {
            const files = ev.target.files ? Array.from(ev.target.files) : [];
            ev.target.value = '';
            validateAndAdd(files);
          }}
        />
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Root>
  );
}

interface FileDropZoneProps {
  disabled: boolean;
  rootProps: ReturnType<ReturnType<typeof useDropzone>['getRootProps']>;
  inputProps: ReturnType<ReturnType<typeof useDropzone>['getInputProps']>;
  isDragActive: boolean;
  onBrowseClick: () => void;
}

function FileDropZone(props: FileDropZoneProps) {
  return (
    <DropZoneContainer {...props.rootProps} isActive={props.isDragActive} isDisabled={props.disabled}>
      <input {...props.inputProps} />
      <Button
        variant="secondarySmall"
        icon={UploadWhite}
        callback={props.disabled ? undefined : props.onBrowseClick}
        disabled={props.disabled}
        skipResponsive
      >
        Importer des fichiers
      </Button>
      <Text fsize="sm">ou</Text>
      <Text fsize="md">
        {props.isDragActive ? 'Déposez vos fichiers ici' : 'Glisser-déposer vos fichiers ici'}
      </Text>
    </DropZoneContainer>
  );
}

function FormatsHelperText() {
  return (
    <HelperText>
      <span>Formats acceptés : {ACCEPTED_EXTENSIONS_LABEL}</span>
      <span>Taille maximale : 1 Mo. Dans la limite de {MATERIAL_SHEET_DOCUMENTS_MAX_FILES} documents</span>
    </HelperText>
  );
}

function InitialLoader() {
  const { containerProps, indicatorEl } = useLoading({
    loading: true,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    indicator: <div className="lds-dual-ring" />,
  });
  return <LoadingDropZone {...containerProps}>{indicatorEl}</LoadingDropZone>;
}

const DropZoneContainer = styled.div<{ isActive: boolean; isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  border: 1.5px dashed ${(p) => (p.isActive ? StyleData.color.secondary : '#c7c9d9')};
  border-radius: ${StyleData.borderRadius.lg};
  background-color: ${(p) => (p.isActive ? '#f3f4f6' : '#ffffff')};
  opacity: ${(p) => (p.isDisabled ? 0.6 : 1)};
  text-align: center;
`;

const LoadingDropZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border: 1.5px dashed #c7c9d9;
  border-radius: ${StyleData.borderRadius.lg};
  background-color: #ffffff;
`;
